var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import customizableComponent from '../../../core/hocs/customization';
import useElement from '../../../core/hooks/useElement';
import useUserQuery from '../../../core/hooks/useUserQuery';
import Select from '../../../core/components/Select';
import UserHeader from '../../../social/components/UserHeader';
import UserChip from '../../../core/components/UserChip';
import withSDK from '../../../core/hocs/withSDK';
import { LANGUAGE_METADATA, BUSINESS_TYPE_METADATA } from '../../../social/constants';
import { Selector, UserSelectorInput } from './styles';
import useUserFilters from '../../../core/hooks/useUserFilters';
const UserSelector = ({ value: userIds = [], onChange = () => { }, parentContainer = null, currentUserId, usersToOmit = [], }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [queriedUsers = []] = useUserQuery(query);
    const { formatMessage } = useIntl();
    const { localeLanguage, businessType } = useUserFilters(currentUserId);
    const queriedUsersFilters = queriedUsers.reduce((acc, user) => {
        var _a, _b, _c, _d;
        return (Object.assign(Object.assign({}, acc), { [user.userId]: {
                localeLanguage: (_b = (_a = user.metadata) === null || _a === void 0 ? void 0 : _a[LANGUAGE_METADATA]) !== null && _b !== void 0 ? _b : ['en'],
                businessType: (_d = (_c = user.metadata) === null || _c === void 0 ? void 0 : _c[BUSINESS_TYPE_METADATA]) !== null && _d !== void 0 ? _d : 'B2C',
            } }));
    }, {});
    const usersToOmitSet = new Set(usersToOmit.map((u) => u.userId));
    const options = queriedUsers
        .filter(({ displayName, userId }) => (displayName === null || displayName === void 0 ? void 0 : displayName.toLowerCase().includes(query === null || query === void 0 ? void 0 : query.toLowerCase())) && userId !== currentUserId)
        .filter(({ userId }) => !usersToOmitSet.has(userId))
        .filter(({ userId }) => {
        const { localeLanguage: otherLocaleLanguage, businessType: otherBusinessType } = queriedUsersFilters[userId];
        return (localeLanguage.some((lang) => otherLocaleLanguage === null || otherLocaleLanguage === void 0 ? void 0 : otherLocaleLanguage.includes(lang)) &&
            businessType === otherBusinessType);
    })
        .map(({ displayName, userId }) => ({
        name: displayName,
        value: userId,
    }));
    const selectedUsers = userIds.map((userId) => {
        var _a;
        return ({
            name: ((_a = queriedUsers.find((user) => user.userId === userId)) !== null && _a !== void 0 ? _a : {}).displayName,
            value: userId,
        });
    });
    const close = () => {
        setIsOpen(false);
        // clear text input on close
        setQuery('');
    };
    const [inputRef, inputElement] = useElement();
    const handleChange = (value) => {
        setQuery(value);
        // open dropdown only when there some data in the text input
        setIsOpen(!!value);
        if (value) {
            inputElement.focus();
        }
    };
    const itemRenderer = ({ value: userId }) => React.createElement(UserHeader, { userId: userId });
    const triggerRenderer = (_a) => {
        var { selected, remove } = _a, props = __rest(_a, ["selected", "remove"]);
        return (React.createElement(Selector, Object.assign({}, props),
            selected.map(({ value: userId }) => (React.createElement(UserChip, { key: userId, userId: userId, onRemove: () => remove(userId, onChange) }))),
            React.createElement(UserSelectorInput, { ref: inputRef, "data-qa-anchor": "user-selector-input", type: "text", value: query, placeholder: formatMessage({ id: 'UserSelector.placeholder' }), onChange: (e) => handleChange(e.target.value) })));
    };
    return (React.createElement(Select, { "data-qa-anchor": "user", value: selectedUsers, 
        // prevent show dropdown for empty query
        options: query ? options : [], renderTrigger: triggerRenderer, renderItem: itemRenderer, parentContainer: parentContainer, isOpen: isOpen, handleClose: close, multiple: true, onSelect: ({ value }) => {
            onChange([...userIds, value]);
            inputElement.focus();
            // clear input on select item
            setQuery('');
        } }));
};
UserSelector.propTypes = {
    value: PropTypes.arrayOf(PropTypes.string),
    parentContainer: PropTypes.element,
    currentUserId: PropTypes.string.isRequired,
    onChange: PropTypes.func,
};
export default memo(withSDK(customizableComponent('UserSelector', UserSelector)));
//# sourceMappingURL=index.js.map